import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useController } from 'react-hook-form';
import { taxCodeRegexp } from '../../helpers/regexp';
import Parser from '../../helpers/taxCode/classes/parser.class';
import { DateDay } from '../../helpers/taxCode/date-utils/date-day.type';
import DateMonth from '../../helpers/taxCode/date-utils/date-month.type';
import { useLanguage } from '../../locale/useLanguage';
import { GetGeoInfoParams } from '../../models/address.models';
import { Gender, Profile } from '../../models/user.models';

export type CompleteProfileForm = {
  name: string;
  surname: string;
  gender: Gender;
  fiscalCode: string;
  dateOfBirth: string;
  placeOfBirth: string;
  citizenship: string;
  residentialAddress: string;
  postcode?: string;
  municipality?: string;
  province?: string;
  countryCode: string;
};

export const useCompleteUserInfoPrivateValidationForm = (
  profile?: Profile,
  geoInfoUpdater?: ({
    country,
    state,
    city,
    postalCode,
  }: GetGeoInfoParams) => void
) => {
  const [t] = useLanguage();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
    watch,
    setError,
  } = useForm<CompleteProfileForm>();

  const watchCountryCode = watch('countryCode', undefined);
  const watchState = watch('province', undefined);
  const watchCity = watch('municipality', undefined);

  const [fiscalValue, setFiscalValue] = useState();

  useEffect(() => {
    if (watchCountryCode !== 'IT') setValue('province', undefined);
  }, [watchCountryCode, setValue]);

  const firstNameValidator = useMemo(
    () => ({
      ...register('name', {
        required: t('completeUserInfoView.firstNameRequiredError'),
      }),
    }),
    [register, t]
  );

  const lastNameValidator = useMemo(
    () => ({
      ...register('surname', {
        required: t('completeUserInfoView.lastNameRequiredError'),
      }),
    }),
    [register, t]
  );

  const postCodeValidator = useMemo(
    () => ({
      ...register('postcode', {
        required: t('completeUserInfoView.requiredField'),
        disabled: !watchCity,
        onChange: ({ target: { value } }) => {
          setValue('postcode', value);
        },
      }),
    }),
    [register, setValue, t, watchCity]
  );

  const { field: municipalityField, fieldState: municipalityFieldState } =
    useController<CompleteProfileForm, 'municipality'>({
      name: 'municipality',
      control,
      rules: { required: t('completeUserInfoView.requiredField') },
    });

  useEffect(() => {
    if (profile && !municipalityFieldState.isDirty) {
      profile.municipality && municipalityField.onChange(profile.municipality);
    }
  }, [municipalityField, municipalityFieldState, profile]);

  const municipalityValidator = useMemo(() => {
    return {
      ...municipalityField,
      onChange: (event: any) => {
        typeof geoInfoUpdater === 'function' &&
          watchCountryCode === 'IT' &&
          geoInfoUpdater({
            country: watchCountryCode,
            state: watchState,
            city: event.target.value,
            postalCode: '',
          });
        setValue('postcode', undefined);
        municipalityField.onChange(event.target.value);
      },
      control: control,
      disabled: !watchState || !watchCountryCode,
    };
  }, [
    control,
    geoInfoUpdater,
    municipalityField,
    setValue,
    watchCountryCode,
    watchState,
  ]);

  const provinceValidator = useMemo(
    () => ({
      ...register('province', {
        required:
          watchCountryCode === 'IT'
            ? t('completeUserInfoView.requiredField')
            : false,
        disabled: !watchCountryCode,
        onChange: ({ target: { value } }) => {
          typeof geoInfoUpdater === 'function' &&
            watchCountryCode === 'IT' &&
            geoInfoUpdater({
              country: watchCountryCode,
              state: value,
              city: '',
            });
          setValue('province', value);
          setValue('municipality', undefined);
          setValue('postcode', undefined);
        },
      }),
    }),
    [geoInfoUpdater, register, setValue, t, watchCountryCode]
  );

  const countryCodeValidator = useMemo(
    () => ({
      ...register('countryCode', {
        required: t('completeUserInfoView.requiredField'),
        onChange: ({ target: { value } }) => {
          typeof geoInfoUpdater === 'function' &&
            geoInfoUpdater({ country: value, state: '' });
          setValue('countryCode', value);
          setValue('province', undefined);
          setValue('municipality', undefined);
          municipalityField.onChange(undefined);
          setError('municipality', {});
          setValue('postcode', undefined);
          setError('postcode', {});
        },
      }),
    }),
    [geoInfoUpdater, municipalityField, register, setError, setValue, t]
  );

  const calculateTaxCode = useCallback(() => {
    const { name, surname, gender, placeOfBirth, dateOfBirth } = getValues();
    const parsedDate = dayjs(dateOfBirth);

    const calculatedCf = Parser.encodeCf({
      firstName: name,
      lastName: surname,
      gender: gender === Gender.Male ? 'M' : 'F',
      day: parsedDate.date() as DateDay,
      month: parsedDate.month() as DateMonth,
      year: parsedDate.year(),
      place: placeOfBirth,
    });

    return calculatedCf;
  }, [getValues]);

  const taxCodeValidator = useMemo(
    () => ({
      ...register('fiscalCode', {
        required: t('completeUserInfoView.taxCodeRequiredError'),
        pattern: {
          value: taxCodeRegexp,
          message: t('completeUserInfoView.taxCodeNotValid'),
        },
        value: fiscalValue,
        onChange: ({ target: { value } }) => {
          setFiscalValue(value.toUpperCase());
          setValue('fiscalCode', value.toUpperCase());
        },
        validate: (val) => {
          const calculatedCf = calculateTaxCode();

          if (calculatedCf !== val.toUpperCase())
            return t('completeUserInfoView.taxCodeNotValid');

          return true;
        },
      }),
    }),
    [calculateTaxCode, fiscalValue, register, setValue, t]
  );

  const placeOfBirthValidator = useMemo(
    () => ({
      ...register('placeOfBirth', {
        required: t('completeUserInfoView.placeOfBirthRequiredError'),
      }),
    }),
    [register, t]
  );

  const dateOfBirthValidator = useMemo(
    () => ({
      ...register('dateOfBirth', {
        required: t('completeUserInfoView.dateOfBirthRequiredError'),
        validate: (val) => {
          const date = dayjs(val).diff(dayjs(), 'minute');
          return date < 0;
        },
        valueAsDate: true,
      }),
    }),
    [register, t]
  );

  const citizenshipValidator = useMemo(
    () => ({
      ...register('citizenship', {
        required: t('completeUserInfoView.citizenshipRequiredError'),
      }),
    }),
    [register, t]
  );

  const residentialAddressValidator = useMemo(
    () => ({
      ...register('residentialAddress', {
        required: t('completeUserInfoView.billingAddressRequiredError'),
      }),
    }),
    [register, t]
  );

  const { field: genderField, fieldState: genderFieldState } =
    useController<CompleteProfileForm>({
      name: 'gender',
      control,
      rules: { required: t('completeUserInfoView.sexRequiredError') },
    });

  useEffect(() => {
    if (profile && !genderFieldState.isDirty) {
      profile.gender && genderField.onChange(profile.gender);
    }
  }, [genderField, genderFieldState.isDirty, profile]);

  const getCorrectCountryInfo = useCallback(async () => {
    if (
      typeof geoInfoUpdater === 'function' &&
      profile &&
      profile.countryCode
    ) {
      if (profile.countryCode === 'IT') {
        await geoInfoUpdater({ country: profile.countryCode, state: '' });
        if (profile.province) {
          await geoInfoUpdater({
            country: profile.countryCode,
            state: profile.province,
            city: '',
          });
        }
        if (profile.municipality) {
          await geoInfoUpdater({
            country: profile.countryCode,
            state: profile.province,
            city: profile.municipality,
            postalCode: '',
          });
        }
      }
    }
  }, [geoInfoUpdater, profile]);

  useEffect(() => {
    if (profile) {
      const getCorrectCountryInfoAsync = async () => {
        if (typeof geoInfoUpdater === 'function') {
          await getCorrectCountryInfo();
        }

        profile.name && setValue('name', profile.name);
        profile.surname && setValue('surname', profile.surname);
        profile.gender && setValue('gender', profile.gender);
        profile.dateOfBirth &&
          setValue(
            'dateOfBirth',
            dayjs(profile.dateOfBirth).format('YYYY-MM-DD')
          );
        profile.placeOfBirth && setValue('placeOfBirth', profile.placeOfBirth);
        profile.citizenship && setValue('citizenship', profile.citizenship);
        profile.fiscalCode && setValue('fiscalCode', profile.fiscalCode);
        profile?.countryCode && setValue('countryCode', profile.countryCode);
        profile.province && setValue('province', profile.province);
        profile.municipality && setValue('municipality', profile.municipality);
        profile.postcode && setValue('postcode', profile.postcode);

        profile.residentialAddress &&
          setValue('residentialAddress', profile.residentialAddress);
      };
      getCorrectCountryInfoAsync();
    }
  }, [geoInfoUpdater, getCorrectCountryInfo, profile, setError, setValue, t]);

  return {
    watchCountryCode,
    firstNameValidator,
    citizenshipValidator,
    dateOfBirthValidator,
    placeOfBirthValidator,
    taxCodeValidator,
    lastNameValidator,
    residentialAddressValidator,
    postCodeValidator,
    countryCodeValidator,
    provinceValidator,
    municipalityValidator,
    municipalityFieldState,
    genderField,
    genderFieldState,
    errors,
    handleSubmit,
    getValues,
    setValue,
    calculateTaxCode,
  };
};
